/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import {
  AvailableDevice,
  FetchDeviceProperties,
  FetchDeviceOnlyScheduleProperties,
  DevicePointsInterface,
  FilterDeviceInterFace,
  DeviceInitialProperties,
  RequestDeviceInterFace,
} from '../../interface/device-management.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  ReplaceValueInFilter,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import deviceApi from '../../utils/api/device';

// a thunk to fetch devices
export const fetchAllDevices = createAsyncThunk<FetchDeviceProperties, RequestDeviceInterFace>(
  'device-management/fetchAllDevices',
  async (data: RequestDeviceInterFace, { dispatch }) => {
    try {
      const response = await deviceApi.fetchDevices(removeEmptyKeys(data));
      return {
        ...response.data,
        results: response.data.results.map((deviceData: AvailableDevice) => ({
          ...deviceData,
          id: deviceData._id,
          connector1: deviceData.connectorsStatus.connector1,
          connector2: deviceData.connectorsStatus.connector2,
        })),
      };
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchListFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('device-management/fetchListFilters', async (_, { dispatch }) => {
  try {
    const response = await deviceApi.fetchListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// a thunk to fetch all stations
export const fetchAllDevicesList = createAsyncThunk<
  { _id: string; chargingPointSn: string }[],
  FetchDeviceOnlyScheduleProperties
>('device-management/fetchAllDevicesList', async (data: FetchDeviceOnlyScheduleProperties) => {
  const response = await deviceApi.fetchAllDevicesList(data.id);
  return response.data;
});

// a thunk to fetch all devices
export const fetchOneDevicePoints = createAsyncThunk<
  { _id: string; connectors: DevicePointsInterface[] },
  FetchDeviceOnlyScheduleProperties
>('device-management/fetchOneDevicePoints', async (data: FetchDeviceOnlyScheduleProperties) => {
  const response = await deviceApi.fetchOneDevicePoints(data.id);
  return response.data;
});

// a thunk to fetch all deviceModels
export const fetchAllDeviceModels = createAsyncThunk<string[]>(
  'device-management/fetchAllDeviceModels',
  async () => {
    const response = await deviceApi.fetchAllDeviceModels();
    return response.data;
  },
);

const deviceFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: DeviceInitialProperties = {
  devices: [],
  allDevices: [],
  allDeviceModels: [],
  devicePoints: [],
  deviceId: '',
  loading: false,
  page: DefaultApiLimit.page,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  filter: {},
  filters: deviceFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const deviceSlice = createSlice({
  name: 'device-management',
  initialState,
  reducers: {
    /**
     * Change the device point status function
     * @param state
     * @param action
     */
    setDeviceFilterData: (
      state: DeviceInitialProperties,
      action: PayloadAction<FilterDeviceInterFace>,
    ) => {
      state.filter = action.payload;
    },
    /**
     * Change the device point status function
     * @param state
     * @param action
     */
    changeDevicePointStatus: (state, action) => {
      state.devicePoints = state.devicePoints.map((value) => {
        const output = value;
        if (value.connector === action.payload.id) {
          output.isReservationEnabled = action.payload.value === 'available';
        }
        return {
          ...output,
        };
      });
    },
    /**
     * Update device management filters
     * @param state
     * @param action
     */
    updateDeviceFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      } else if (payload.type === 'replace') {
        const { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      }
    },
    /**
     * Change the device point status function
     * @param state
     * @param action
     */
    removeDevicePointStatus: (state) => {
      state.devicePoints = [];
      state.deviceId = '';
    },
    /**
     * Function that reset slice to initial state
     * @param state
     */
    resetDeviceToInitialState: (state) => {
      Object.assign(state, initialState);
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<DeviceInitialProperties>) => {
    builder
      .addCase(fetchAllDevices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllDevices.fulfilled, (state, action: PayloadAction<FetchDeviceProperties>) => {
        state.loading = false;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.devices = action.payload.results;
      })
      .addCase(fetchAllDevices.rejected, (state) => {
        state.loading = false;
      });
    builder.addCase(fetchAllDevicesList.fulfilled, (state, action) => {
      state.loading = false;
      state.allDevices = action.payload.map(
        (stationData: { _id: string; chargingPointSn: string }) => ({
          value: stationData._id,
          label: stationData.chargingPointSn,
        }),
      );
    });
    builder.addCase(fetchOneDevicePoints.fulfilled, (state, action) => {
      state.loading = false;
      state.devicePoints = action.payload.connectors;
      state.deviceId = action.payload._id;
    });
    builder.addCase(fetchAllDeviceModels.fulfilled, (state, action) => {
      state.loading = false;
      state.allDeviceModels = action.payload.map((device: string) => ({
        value: device,
        label: device,
      }));
    });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  changeDevicePointStatus,
  removeDevicePointStatus,
  setDeviceFilterData,
  resetDeviceToInitialState,
  updateDeviceFilters,
  setSortData,
} = deviceSlice.actions;

// Export the reducer
export default deviceSlice.reducer;
